// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Select, Option, Checkbox } from 'informed';

import {
  addInfantMeasurement,
  deleteInfantMeasurement,
  updateInfantMeasurement,
  updateRequiredMeasurement,
} from '../../../../actions/growthChartActions';

import DateText from '../../../DateText.jsx';
import NumericText from '../../../NumericText.jsx';
import Table from '../../../Table.jsx';
import { Error, Warning } from '../../../Typography.jsx';

type Props = {
  dob: string,
  measurements: Object[],
  activePatientId: string,
  addInfantMeasurement: Function,
  deleteInfantMeasurement: Function,
  updateInfantMeasurement: Function,
  updateRequiredMeasurement: Function,
}

class InfantMeasurementsComponent extends Component<Props> {
  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.date = moment(formValues.date, 'DD-MM-YYYY').toISOString();
  }

  transformMeasurements(formValues) {
    const {
      weightUnit, lengthUnit, headCircumferenceUnit,
    } = formValues;
    if (weightUnit === 'lb') {
      formValues.weight = (formValues.weight / 2.2).toFixed(1);
    }
    if (lengthUnit === 'in') {
      formValues.length = (formValues.length / 0.3937).toFixed(1);
    }
    if (headCircumferenceUnit === 'in') {
      formValues.headCircumference = (formValues.headCircumference / 0.3937).toFixed(1);
    }
  }

  handleDeleteRow = (id: string) => {
    this.props.deleteInfantMeasurement(this.props.activePatientId, id);
  }

  showDelete = (row: Object) => row.showDelete !== false;

  handleSaveRow = (id: string, formData: Object) => {
    this.transformDates(formData.values);
    this.transformMeasurements(formData.values);
    if (formData.values.required) {
      if (formData.values.overridden) {
        formData.values.weight = null;
      }
      this.props.updateRequiredMeasurement(this.props.activePatientId, {
        measurementId: id,
        ...formData.values,
      });
    } else {
      this.props.updateInfantMeasurement(this.props.activePatientId, {
        measurementId: id,
        ...formData.values,
      });
    }
  }

  showEdit = (row: Object) => row.showEdit !== false;

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    this.props.addInfantMeasurement(this.props.activePatientId, formData.values);
  }

  extraActions = (row: Object) => (row.showEdit === false ? '(from WCC)' : '');

  render() {
    const CRITICAL_LOW_PERCENTILE_THRESHOLD = 3;
    const LOW_PERCENTILE_THRESHOLD = 10;
    const CRITICAL_HIGH_PERCENTILE_THRESHOLD = 90;

    const columns = [
      {
        Header: 'Date',
        id: 'date',
        cell: item => (item.date ? moment(item.date).format() : '--'),
        // hidden state value on whether required measurement to control handleSaveRow
        editCell: item => <span>
          <DateText field='date' item={item} />
          {item.dateDue && <Checkbox field='required' initialValue={true} style={{ display: 'none' }} />}
        </span>,
      },
      {
        Header: 'Age',
        id: 'age',
        defaultSortDesc: true,
        cell: item => (item.date && !item.overridden ? `${item.age} m` : '--'),
        width: 60,
      },
      {
        Header: 'Weight',
        id: 'weight',
        cell: (item) => {
          if (item.overridden) {
            return '(overridden)';
          }
          if (item.required && moment().isAfter(item.dateDue) && !item.weight) {
            return <Error>(overdue)</Error>;
          }
          return item.weight ? `${item.weight} kg (${(item.weight * 2.2).toFixed(1)} lb)` : '--';
        },
        editCell: item => (
          <div>
            <NumericText field='weight' initialValue={item.weight} size={4} />
            <Select field='weightUnit'>
              <Option value='kg'>kg</Option>
              <Option value='lb'>lb</Option>
            </Select>
            {item.dateDue && <div><Checkbox field='overridden' initialValue={item.overridden} />Override</div>}
          </div>
        ),
      },
      {
        Header: 'Percentile',
        id: 'weightPercentile',
        cell: (item) => {
          if (item.weightPercentile === undefined || item.weightPercentile === null) {
            return '--';
          }
          if (item.weightPercentile < CRITICAL_LOW_PERCENTILE_THRESHOLD
            || item.weightPercentile > CRITICAL_HIGH_PERCENTILE_THRESHOLD) {
            return <Error>{item.weightPercentile}</Error>;
          }
          if (item.weightPercentile <= LOW_PERCENTILE_THRESHOLD) {
            return <Warning>{item.weightPercentile}</Warning>;
          }
          return item.weightPercentile;
        },
        width: 80,
      },
      {
        Header: 'Length',
        id: 'length',
        cell: item => (item.length ? `${item.length} cm (${(item.length / 2.54).toFixed(1)} in)` : '--'),
        editCell: item => (
          <div>
            <NumericText field='length' initialValue={item.length} size={4} />
            <Select field='lengthUnit'>
              <Option value='cm'>cm</Option>
              <Option value='in'>in</Option>
            </Select>
          </div>
        ),
      },
      {
        Header: 'Percentile',
        id: 'lengthPercentile',
        width: 80,
        cell: (item) => {
          if (item.lengthPercentile === undefined || item.lengthPercentile === null) {
            return '--';
          }
          if (item.lengthPercentile < CRITICAL_LOW_PERCENTILE_THRESHOLD
            || item.lengthPercentile > CRITICAL_HIGH_PERCENTILE_THRESHOLD) {
            return <Error>{item.lengthPercentile}</Error>;
          }
          if (item.lengthPercentile <= LOW_PERCENTILE_THRESHOLD) {
            return <Warning>{item.lengthPercentile}</Warning>;
          }
          return item.lengthPercentile;
        },
      },
      {
        Header: 'Head Circumference',
        id: 'headCircumference',
        cell: item => (item.headCircumference ? `${item.headCircumference} cm (${(item.headCircumference / 2.54).toFixed(1)} in)` : '--'),
        editCell: item => (
          <div>
            <NumericText field='headCircumference' initialValue={item.headCircumference} size={4} />
            <Select field='headCircumferenceUnit'>
              <Option value='cm'>cm</Option>
              <Option value='in'>in</Option>
            </Select>
          </div>
        ),
      },
      {
        Header: 'Percentile',
        id: 'hcPercentile',
        width: 80,
        cell: (item) => {
          if (item.hcPercentile === undefined || item.hcPercentile === null) {
            return '--';
          }
          if (item.hcPercentile < CRITICAL_LOW_PERCENTILE_THRESHOLD
            || item.hcPercentile > CRITICAL_HIGH_PERCENTILE_THRESHOLD) {
            return <Error>{item.hcPercentile}</Error>;
          }
          if (item.hcPercentile <= LOW_PERCENTILE_THRESHOLD) {
            return <Warning>{item.hcPercentile}</Warning>;
          }
          return item.hcPercentile;
        },
      },
      {
        Header: 'Date Due',
        id: 'dateDue',
        cell: item => (item.dateDue ? moment(item.dateDue).format() : '--'),
      },
    ];

    return (
      <Table
        columns={columns}
        data={this.props.measurements}
        handleDeleteRow={this.handleDeleteRow}
        handleSaveRow={this.handleSaveRow}
        handleSaveNewRow={this.handleSaveNewRow}
        showDelete={this.showDelete}
        showEdit={this.showEdit}
        extraActions={this.extraActions}
      />
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
});

const mapDispatchToProps = dispatch => ({
  addInfantMeasurement:
    (patientId, measurement) => dispatch(addInfantMeasurement(patientId, measurement)),
  deleteInfantMeasurement:
    (patientId, measurementId) => dispatch(deleteInfantMeasurement(patientId, measurementId)),
  updateInfantMeasurement:
    (patientId, measurement) => dispatch(updateInfantMeasurement(patientId, measurement)),
  updateRequiredMeasurement:
    (patientId, measurement) => dispatch(updateRequiredMeasurement(patientId, measurement)),
});

const InfantMeasurements = connect(
  mapStateToProps,
  mapDispatchToProps,
)(InfantMeasurementsComponent);

export default InfantMeasurements;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/growthChart/infant/InfantMeasurements.jsx